import { IClubActivityPage } from '@/interfaces/IClubActivity';
import {
  IClubMember,
  IClubOrderMemberContext,
} from '@/interfaces/IClubMember';
import { IClubProgram, IUpsertClubProgramDto } from '@/interfaces/IClubProgram';
import { IClubReportsSummary } from '@/interfaces/IClubReportsSummary';
import { IDataOptions } from '@/interfaces/IDataOptions';
import { IPaginated } from '@/interfaces/IPaginated';
import { Globals } from '@/modules/Globals';
import axios from 'axios';

const apiUrl = Globals.apiPath + '/club';

export interface IClubAdjustPointsDto {
  points: number;
  reason: string;
  idempotencyKey: string;
}

export interface IClubAdjustCashDto {
  amount: number;
  bucket: 'principal' | 'bonus';
  reason: string;
  idempotencyKey: string;
}

export interface IClubRedeemDto {
  orderId: number;
  points: number;
  /**
   * Omitted when the cashier holds a grant bound to this order (a code
   * consumed for it).
   */
  code?: string;
  idempotencyKey: string;
}

export interface IClubRedeemResult {
  replayed: boolean;
  member: IClubMember;
  /** Points actually redeemed (may be clamped below the requested amount). */
  points: number;
  /** Discount value created on the order. */
  value: number;
}

export interface IClubOrderAttachDto {
  memberId: number;
  /**
   * Live member code, when the cashier saw one: consumed for the order;
   * grants this cashier code-less spends on it.
   */
  code?: string;
}

export interface IClubTopupDto {
  restaurantId: number;
  branchId: number;
  memberId: number;
  amount: number;
  planIndex: number | null;
  paymentMethod: number;
  cajaId: number | null;
  idempotencyKey: string;
}

/** Named filters of the program-wide movements feed (server whitelist). */
export type TClubActivityKind = 'adjust' | 'topup' | 'redeem' | 'reversal';
export type TClubActivitySource = 'points' | 'cash';

/** Sort keys accepted by GET /club/members (mirrors the server whitelist). */
export const CLUB_MEMBER_SORT_KEYS = [
  'name',
  'memberNumber',
  'pointsBalance',
  'cashTotal',
  'lifetimePoints',
  'lifetimeSpend',
  'visitsCount',
  'lastVisitAt',
  'createdAt',
] as const;
export type TClubMemberSortKey = typeof CLUB_MEMBER_SORT_KEYS[number];

export interface IClubActivityParams {
  cursor?: string | null;
  kind?: TClubActivityKind | null;
  source?: TClubActivitySource | null;
  dateFrom?: Date | null;
  dateTo?: Date | null;
  /** Page size; the server clamps to 1..50 (default 30). */
  limit?: number | null;
}

/**
 * 428 CLUB_CODE_REQUIRED: the staff identification grant no longer covers
 * the action (expired, bound to another order, other member). Callers fall
 * back to asking for the member's code instead of showing an error.
 */
export function isClubCodeRequiredError(error: unknown): boolean {
  if (error == null || !axios.isAxiosError(error)) return false;
  return error.response?.status === 428;
}

export class ClubService {
  //#region Program

  /** Returns the restaurant's program, or null when it was never activated. */
  static async getProgram(restaurantId: number): Promise<IClubProgram | null> {
    const res = await axios.get<IClubProgram | ''>(
      `${apiUrl}/program?r=${restaurantId}`
    );
    return res.data || null;
  }

  static async saveProgram(
    restaurantId: number,
    dto: IUpsertClubProgramDto
  ): Promise<IClubProgram> {
    const res = await axios.post<IClubProgram>(
      `${apiUrl}/program?r=${restaurantId}`,
      dto
    );
    return res.data;
  }

  //#endregion

  //#region Members

  static async getMembers(
    restaurantId: number,
    options?: IDataOptions
  ): Promise<IPaginated<IClubMember>> {
    const res = await axios.get<IPaginated<IClubMember>>(
      `${apiUrl}/members?r=${restaurantId}` +
        (options
          ? `&options=${encodeURIComponent(JSON.stringify(options))}`
          : '')
    );
    return res.data;
  }

  static async getMember(id: number): Promise<IClubMember> {
    const res = await axios.get<IClubMember>(`${apiUrl}/members/${id}`);
    return res.data;
  }

  static async getMemberActivity(
    id: number,
    cursor?: string | null,
    filter?: 'adjust'
  ): Promise<IClubActivityPage> {
    const params = new URLSearchParams();
    if (cursor) params.set('cursor', cursor);
    if (filter) params.set('filter', filter);
    const query = params.toString();

    const res = await axios.get<IClubActivityPage>(
      `${apiUrl}/members/${id}/activity` + (query ? `?${query}` : '')
    );
    return res.data;
  }

  static async adjustPoints(
    id: number,
    dto: IClubAdjustPointsDto
  ): Promise<IClubMember> {
    const res = await axios.post<IClubMember>(
      `${apiUrl}/members/${id}/points-adjust`,
      dto
    );
    return res.data;
  }

  static async adjustCash(
    id: number,
    dto: IClubAdjustCashDto
  ): Promise<IClubMember> {
    const res = await axios.post<IClubMember>(
      `${apiUrl}/members/${id}/balance-adjust`,
      dto
    );
    return res.data;
  }

  static async blockMember(id: number): Promise<IClubMember> {
    const res = await axios.post<IClubMember>(
      `${apiUrl}/members/${id}/block`,
      {}
    );
    return res.data;
  }

  static async unblockMember(id: number): Promise<IClubMember> {
    const res = await axios.post<IClubMember>(
      `${apiUrl}/members/${id}/unblock`,
      {}
    );
    return res.data;
  }

  //#endregion

  //#region Program activity (audit)

  /** Program-wide movements feed ("Movimientos" tab) — managers only. */
  static async getActivity(
    restaurantId: number,
    params: IClubActivityParams
  ): Promise<IClubActivityPage> {
    const query = new URLSearchParams({ r: String(restaurantId) });
    if (params.cursor) query.set('cursor', params.cursor);
    if (params.kind) query.set('kind', params.kind);
    if (params.source) query.set('source', params.source);
    if (params.dateFrom) query.set('dateFrom', params.dateFrom.toISOString());
    if (params.dateTo) query.set('dateTo', params.dateTo.toISOString());
    if (params.limit) query.set('limit', String(params.limit));

    const res = await axios.get<IClubActivityPage>(
      `${apiUrl}/activity?${query.toString()}`
    );
    return res.data;
  }

  //#endregion

  //#region Cashier identification

  /**
   * Resolves a member code WITHOUT consuming it — pure preview, no grant. The
   * code must travel with the create / attach / redeem / payment call, which
   * consumes it for that one order.
   */
  static async identify(
    restaurantId: number,
    code: string
  ): Promise<IClubMember> {
    const res = await axios.post<IClubMember>(`${apiUrl}/identify`, {
      restaurantId,
      code,
    });
    return res.data;
  }

  /** Attached member figures + live identification grant for an order. */
  static async getOrderMemberContext(
    orderId: number
  ): Promise<IClubOrderMemberContext> {
    const res = await axios.get<IClubOrderMemberContext>(
      `${apiUrl}/orders/${orderId}/member`
    );
    return res.data;
  }

  /**
   * Links a member to an order created without one. With the member's live
   * `code` (consumed) the cashier also gets a grant bound to the order (next
   * redeem / balance payment skips the code). 409 when another member holds
   * the order.
   */
  static async attachToOrder(
    orderId: number,
    dto: IClubOrderAttachDto
  ): Promise<IClubOrderMemberContext> {
    const res = await axios.post<IClubOrderMemberContext>(
      `${apiUrl}/orders/${orderId}/attach`,
      dto
    );
    return res.data;
  }

  static async lookup(
    restaurantId: number,
    params: { search?: string; phone?: string; memberNumber?: number }
  ): Promise<IClubMember[]> {
    const query = new URLSearchParams({ r: String(restaurantId) });
    if (params.search) query.set('search', params.search);
    if (params.phone) query.set('phone', params.phone);
    if (params.memberNumber != null)
      query.set('memberNumber', String(params.memberNumber));

    const res = await axios.get<IClubMember[]>(
      `${apiUrl}/lookup?${query.toString()}`
    );
    return res.data;
  }

  //#endregion

  //#region Redeem / top-up

  static async redeem(dto: IClubRedeemDto): Promise<IClubRedeemResult> {
    const res = await axios.post<IClubRedeemResult>(`${apiUrl}/redeem`, dto);
    return res.data;
  }

  static async topup(dto: IClubTopupDto) {
    const res = await axios.post(`${apiUrl}/topup`, dto);
    return res.data;
  }

  //#endregion

  //#region Reports

  static async getReportsSummary(
    restaurantId: number
  ): Promise<IClubReportsSummary> {
    const res = await axios.get<IClubReportsSummary>(
      `${apiUrl}/reports/summary?r=${restaurantId}`
    );
    return res.data;
  }

  //#endregion
}
